import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { 
  GROWTH_AREAS, 
  DEVELOPMENT_TOPICS,
  GROWTH_AREA_COLORS,
  bookOrder
} from '../utils/constants';

// Enrichment filter keys
const AGE_RELEVANCE_KEYS = [
  'elementary', 'middleSchool', 'highSchool', 'youngAdult', 'middleAge', 'wisdomYears'
];
const RELATIONSHIP_RELEVANCE_KEYS = [
  'youth', 'singleYoungAdult', 'singleAdult', 'dating', 'engaged', 'earlyMarriage', 'experiencedMarriage', 'marriedWithYoungKids', 'marriedWithOlderKids', 'grandparents', 'separated', 'divorced', 'widowed'
];
const DISCIPLESHIP_STAGE_KEYS = [
  'notCurrentlyBeliever', 'newBeliever', 'growingDisciple', 'discipleMaker', 'sageStage'
];
const CHRISTIAN_EXPERIENCE_KEYS = [
  'noExperienceYet', 'newToFaith', 'grewUpStillWalking', 'cameToFaithNowWalking', 'crisisLeaningIn', 'crisisLeaningOut', 'walkedAwayCurrently', 'walkedAwayNowOpen', 'walkedAwayNowReturned'
];

// Helper to normalize tags/categories to array
const toArray = (val) => Array.isArray(val) ? val : (typeof val === 'string' ? val.split(",").map(t => t.trim()).filter(Boolean) : []);

const SearchVerseSystem = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGrowthArea, setSelectedGrowthArea] = useState('');
  const [selectedDevTopic, setSelectedDevTopic] = useState('');
  const [selectedBook, setSelectedBook] = useState('');
  const [verses, setVerses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isBookListOpen, setIsBookListOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [versesPerPage] = useState(10);
  const [expandedCards, setExpandedCards] = useState(new Set());
  // Enrichment filters
  const [selectedAge, setSelectedAge] = useState('');
  const [selectedRelationship, setSelectedRelationship] = useState('');
  const [selectedDiscipleship, setSelectedDiscipleship] = useState('');
  const [selectedExperience, setSelectedExperience] = useState('');
  const [availableBooks, setAvailableBooks] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [displayOrder, setDisplayOrder] = useState('book'); // 'book' or 'alpha'
  const [bookFilter, setBookFilter] = useState('');
  const [searchTriggered, setSearchTriggered] = useState(false);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [sortBy, setSortBy] = useState('book'); // 'book', 'alpha', 'date'

  // Get available books from all verses
  const getAvailableBooks = (versesList) => {
    const booksSet = new Set();
    versesList.forEach(verse => {
      const verseBook = verse.reference?.split(' ')[0];
      let bookName = verseBook;
      if (['1', '2', '3'].includes(bookName)) {
        bookName = verse.reference?.split(' ').slice(0, 2).join(' ');
      }
      if (bookName) booksSet.add(bookName);
    });
    return bookOrder.filter(book => booksSet.has(book));
  };

  // Prioritized search logic
  const prioritizedSearch = (results, searchLower) => {
    if (!searchLower) return results;
    // 1. Verse text matches
    const textMatches = results.filter(verse =>
      verse.text?.toLowerCase().includes(searchLower)
    );
    // 2. Meta data matches (tags, categories, contextual meaning, etc)
    const metaMatches = results.filter(verse =>
      !textMatches.includes(verse) && (
        verse.reference?.toLowerCase().includes(searchLower) ||
        toArray(verse.tags).some(tag => tag.toLowerCase().includes(searchLower)) ||
        toArray(verse.categories).some(cat => cat.toLowerCase().includes(searchLower)) ||
        verse.growthArea?.toLowerCase().includes(searchLower) ||
        verse.devTopic?.toLowerCase().includes(searchLower) ||
        verse.concept?.toLowerCase().includes(searchLower)
      )
    );
    return [...textMatches, ...metaMatches];
  };

  // Get available books from all verses (for dropdown)
  useEffect(() => {
    async function fetchBooks() {
      const versesRef = collection(db, 'verses');
      const snapshot = await getDocs(versesRef);
      const results = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const books = getAvailableBooks(results);
      setAvailableBooks(books);
      console.log('Available books loaded:', books);
    }
    fetchBooks();
  }, []);

  // Fetch all verses on mount for default display
  useEffect(() => {
    async function fetchAllVerses() {
      setLoading(true);
      try {
        const versesRef = collection(db, 'verses');
        const snapshot = await getDocs(versesRef);
        const results = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setVerses(results);
      } catch (error) {
        console.error('Error loading verses:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchAllVerses();
  }, []);

  // Update sorting logic
  const sortVerses = (versesToSort) => {
    switch (sortBy) {
      case 'book':
        return [...versesToSort].sort((a, b) => {
          const aRef = parseReference(a.reference);
          const bRef = parseReference(b.reference);
          const aBookIdx = bookOrder.indexOf(aRef.book);
          const bBookIdx = bookOrder.indexOf(bRef.book);
          if (aBookIdx !== bBookIdx) return aBookIdx - bBookIdx;
          if (aRef.chapter !== bRef.chapter) return aRef.chapter - bRef.chapter;
          return aRef.verse - bRef.verse;
        });
      case 'alpha':
        return [...versesToSort].sort((a, b) => (a.reference || '').localeCompare(b.reference || ''));
      case 'date':
        return [...versesToSort].sort((a, b) => {
          const dateA = a.createdAt?.toDate?.() || new Date(0);
          const dateB = b.createdAt?.toDate?.() || new Date(0);
          return dateB - dateA;
        });
      default:
        return versesToSort;
    }
  };

  // Update search logic to use sorting
  const searchVerses = async () => {
    setLoading(true);
    console.log('Search triggered. Filters:', {
      bookFilter,
      selectedAge,
      selectedRelationship,
      selectedDiscipleship,
      selectedExperience,
      selectedGrowthArea,
      selectedDevTopic,
      searchTerm,
      sortBy
    });
    try {
      const versesRef = collection(db, 'verses');
      const snapshot = await getDocs(versesRef);
      let results = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      // Apply filters
      if (bookFilter) {
        results = results.filter(verse => {
          const verseBook = verse.reference?.split(' ')[0];
          let bookName = verseBook;
          if (["1", "2", "3"].includes(bookName)) {
            bookName = verse.reference?.split(' ').slice(0, 2).join(' ');
          }
          return bookName === bookFilter;
        });
      }
      if (selectedAge) {
        results = results.filter(verse => verse.ageRelevance && verse.ageRelevance[selectedAge] >= 0.5);
      }
      if (selectedRelationship) {
        results = results.filter(verse => verse.relationshipRelevance && verse.relationshipRelevance[selectedRelationship] >= 0.5);
      }
      if (selectedDiscipleship) {
        results = results.filter(verse => verse.discipleshipStageRelevance && verse.discipleshipStageRelevance[selectedDiscipleship] >= 0.5);
      }
      if (selectedExperience) {
        results = results.filter(verse => verse.christianExperienceRelevance && verse.christianExperienceRelevance[selectedExperience] >= 0.5);
      }
      if (selectedGrowthArea) {
        results = results.filter(verse => verse.growthArea === selectedGrowthArea);
      }
      if (selectedDevTopic) {
        results = results.filter(verse => verse.devTopic === selectedDevTopic);
      }
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        results = prioritizedSearch(results, searchLower);
      }

      // Apply sorting
      results = sortVerses(results);
      
      setVerses(results);
    } catch (error) {
      console.error('Error searching verses:', error);
    } finally {
      setLoading(false);
    }
  };

  // Update useEffect to trigger search when filters or sort changes
  useEffect(() => {
    if (searchTriggered) {
      searchVerses();
      setSearchTriggered(false);
    }
  }, [searchTriggered, sortBy]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedGrowthArea, selectedDevTopic, selectedBook, selectedAge, selectedRelationship, selectedDiscipleship, selectedExperience]);

  // Pagination helpers
  const indexOfLastVerse = currentPage * versesPerPage;
  const indexOfFirstVerse = indexOfLastVerse - versesPerPage;

  // Helper to extract book, chapter, verse
  const parseReference = (ref) => {
    if (!ref) return { book: '', chapter: 0, verse: 0 };
    const match = ref.match(/^(\d? ?[A-Za-z ]+?) (\d+):(\d+)/);
    if (!match) return { book: ref, chapter: 0, verse: 0 };
    return {
      book: match[1].trim(),
      chapter: parseInt(match[2], 10),
      verse: parseInt(match[3], 10)
    };
  };

  // Sorting logic
  let sortedVerses = [...verses];
  if (displayOrder === 'book') {
    sortedVerses.sort((a, b) => {
      const aRef = parseReference(a.reference);
      const bRef = parseReference(b.reference);
      const aBookIdx = bookOrder.indexOf(aRef.book);
      const bBookIdx = bookOrder.indexOf(bRef.book);
      if (aBookIdx !== bBookIdx) return aBookIdx - bBookIdx;
      if (aRef.chapter !== bRef.chapter) return aRef.chapter - bRef.chapter;
      return aRef.verse - bRef.verse;
    });
  } else if (displayOrder === 'alpha') {
    sortedVerses.sort((a, b) => (a.reference || '').localeCompare(b.reference || ''));
  }
  const currentVerses = showAll ? sortedVerses : sortedVerses.slice(indexOfFirstVerse, indexOfLastVerse);

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const clearFilter = (filterType) => {
    switch(filterType) {
      case 'book': setSelectedBook(''); break;
      case 'growthArea': setSelectedGrowthArea(''); break;
      case 'devTopic': setSelectedDevTopic(''); break;
      default: break;
    }
  };

  const toggleCard = (verseId) => {
    setExpandedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(verseId)) {
        newSet.delete(verseId);
      } else {
        newSet.add(verseId);
      }
      return newSet;
    });
  };

  return (
    <div className="w-full space-y-4 px-4 py-8 md:px-8">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold">System Verse Search (Admin)</h1>
        <Link
          to="/search-bible-verses"
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors duration-200 font-medium shadow-sm"
        >
          Add a Verse
        </Link>
      </div>

      {/* Primary Search Bar and Controls */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="flex-1 relative">
          <input
            type="text"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            onKeyPress={e => e.key === 'Enter' && setSearchTriggered(true)}
            placeholder="Search verses by reference, text, tags, categories..."
            className="w-full p-3 pl-10 border rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <svg
            className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
        <div className="flex gap-2">
          <select
            value={sortBy}
            onChange={e => {
              setSortBy(e.target.value);
              setSearchTriggered(true);
            }}
            className="p-3 border rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="book">Sort by Book</option>
            <option value="alpha">Sort Alphabetically</option>
            <option value="date">Sort by Date Added</option>
          </select>
          <button
            onClick={() => setSearchTriggered(true)}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg shadow-sm hover:bg-indigo-700 transition-colors duration-200 flex items-center gap-2"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            Search
          </button>
          <button
            onClick={() => setIsFiltersOpen(!isFiltersOpen)}
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg shadow-sm flex items-center gap-2 transition-colors duration-200"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            Filters
            <svg
              className={`h-4 w-4 transition-transform duration-200 ${isFiltersOpen ? 'transform rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
      </div>

      {/* Filters Accordion */}
      <div className={`mb-6 transition-all duration-200 ${isFiltersOpen ? 'opacity-100 max-h-[500px]' : 'opacity-0 max-h-0 overflow-hidden'}`}>
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Book Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Book</label>
              <select
                value={bookFilter}
                onChange={e => setBookFilter(e.target.value)}
                className={`w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${bookFilter === '' ? 'text-gray-400' : 'text-gray-900'}`}
              >
                <option value="" className="text-gray-400">All Books</option>
                {bookOrder.map(book => (
                  <option key={book} value={book}>{book}</option>
                ))}
              </select>
            </div>

            {/* Age Group Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Age Group</label>
              <select
                value={selectedAge}
                onChange={e => setSelectedAge(e.target.value)}
                className={`w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${selectedAge === '' ? 'text-gray-400' : 'text-gray-900'}`}
              >
                <option value="" className="text-gray-400">All Age Groups</option>
                {AGE_RELEVANCE_KEYS.map(key => (
                  <option key={key} value={key}>{key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</option>
                ))}
              </select>
            </div>

            {/* Relationship Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Relationship Status</label>
              <select
                value={selectedRelationship}
                onChange={e => setSelectedRelationship(e.target.value)}
                className={`w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${selectedRelationship === '' ? 'text-gray-400' : 'text-gray-900'}`}
              >
                <option value="" className="text-gray-400">All Relationship Statuses</option>
                {RELATIONSHIP_RELEVANCE_KEYS.map(key => (
                  <option key={key} value={key}>{key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</option>
                ))}
              </select>
            </div>

            {/* Discipleship Stage Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Discipleship Stage</label>
              <select
                value={selectedDiscipleship}
                onChange={e => setSelectedDiscipleship(e.target.value)}
                className={`w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${selectedDiscipleship === '' ? 'text-gray-400' : 'text-gray-900'}`}
              >
                <option value="" className="text-gray-400">All Discipleship Stages</option>
                {DISCIPLESHIP_STAGE_KEYS.map(key => (
                  <option key={key} value={key}>{key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</option>
                ))}
              </select>
            </div>

            {/* Christian Experience Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Christian Experience</label>
              <select
                value={selectedExperience}
                onChange={e => setSelectedExperience(e.target.value)}
                className={`w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${selectedExperience === '' ? 'text-gray-400' : 'text-gray-900'}`}
              >
                <option value="" className="text-gray-400">All Christian Experiences</option>
                {CHRISTIAN_EXPERIENCE_KEYS.map(key => (
                  <option key={key} value={key}>{key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</option>
                ))}
              </select>
            </div>

            {/* Growth Area Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Growth Area</label>
              <select
                value={selectedGrowthArea}
                onChange={(e) => setSelectedGrowthArea(e.target.value)}
                className={`w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${selectedGrowthArea === '' ? 'text-gray-400' : 'text-gray-900'}`}
              >
                <option value="" className="text-gray-400">All Growth Areas</option>
                {GROWTH_AREAS.map(area => (
                  <option key={area} value={area}>{area}</option>
                ))}
              </select>
            </div>

            {/* Development Topic Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Development Topic</label>
              <select
                value={selectedDevTopic}
                onChange={(e) => setSelectedDevTopic(e.target.value)}
                className={`w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${selectedDevTopic === '' ? 'text-gray-400' : 'text-gray-900'}`}
              >
                <option value="" className="text-gray-400">All Development Topics</option>
                {DEVELOPMENT_TOPICS.map(topic => (
                  <option key={topic} value={topic}>{topic}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <button
              onClick={() => setSearchTriggered(true)}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg shadow-sm hover:bg-indigo-700 transition-colors duration-200"
            >
              Apply Filters
            </button>
          </div>
        </div>
      </div>

      {/* Results Count */}
      <div className="text-sm text-gray-600 mb-4">
        {loading ? (
          <span>Loading...</span>
        ) : verses.length === 0 ? (
          <span>No verses found</span>
        ) : (
          <span>
            Found {verses.length} verses
            {!showAll && verses.length > versesPerPage && ` (Showing ${indexOfFirstVerse + 1}-${Math.min(indexOfLastVerse, verses.length)})`}
          </span>
        )}
      </div>

      {/* Results Grid */}
      {!loading && verses.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full">
          {currentVerses.map(verse => (
            <div 
              key={verse.id} 
              className="relative border rounded-lg shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden bg-[#f9f9f9]"
            >
              {/* Edit Icon - Always Visible */}
              <Link
                to={`/edit-verse/${verse.id}`}
                className="absolute top-2 right-2 text-gray-400 hover:text-blue-600 z-10"
                onClick={(e) => e.stopPropagation()}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                </svg>
              </Link>
              {/* Clickable Card Content */}
              <div 
                onClick={() => toggleCard(verse.id)}
                className="cursor-pointer p-4"
              >
                <div className="flex justify-between items-start mb-2">
                  <h3 className="text-lg font-semibold text-gray-800">{verse.reference}</h3>
                </div>
                <p className="text-gray-600 mb-3">{verse.text}</p>
                <div className="flex flex-wrap gap-2 mb-2">
                  {verse.growthArea && (
                    <span className={`text-sm px-3 py-1.5 rounded-full ${
                      GROWTH_AREA_COLORS[verse.growthArea]?.tailwindBg || 'bg-gray-100'
                    } ${
                      GROWTH_AREA_COLORS[verse.growthArea]?.tailwindText || 'text-gray-700'
                    }`}>
                      {verse.growthArea}
                    </span>
                  )}
                  {verse.devTopic && (
                    <span className="text-sm px-3 py-1.5 bg-gray-100 text-gray-700 rounded-full">
                      {verse.devTopic}
                    </span>
                  )}
                </div>
                <div className={`mt-4 space-y-3 overflow-hidden transition-all duration-300 ${
                  expandedCards.has(verse.id) ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                }`}>
                  {verse.question && (
                    <div className="border-t pt-3">
                      <p className="text-sm text-gray-700">
                        <span className="font-medium">Reflection:</span> {verse.question}
                      </p>
                    </div>
                  )}
                  {toArray(verse.tags).length > 0 && (
                    <div className="border-t pt-3">
                      <p className="text-xs text-gray-500 mb-1">Tags:</p>
                      <div className="flex flex-wrap gap-1">
                        {toArray(verse.tags).map(tag => (
                          <span key={tag} className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-full">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {toArray(verse.categories).length > 0 && (
                    <div className="border-t pt-3">
                      <p className="text-xs text-gray-500 mb-1">Categories:</p>
                      <div className="flex flex-wrap gap-1">
                        {toArray(verse.categories).map(category => (
                          <span key={category} className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-full">
                            {category}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div className="text-center mt-2">
                  <svg 
                    className={`w-5 h-5 mx-auto text-gray-400 transition-transform duration-200 ${
                      expandedCards.has(verse.id) ? 'transform rotate-180' : ''
                    }`}
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {!showAll && verses.length > versesPerPage && (
        <div className="mt-6 flex flex-col items-center">
          <div className="flex items-center space-x-2">
            <button
              onClick={() => paginate(currentPage - 1)}
              disabled={currentPage === 1}
              className={`p-2 rounded-full ${
                currentPage === 1 ? 'text-gray-400' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
            </button>
            <span className="text-sm text-gray-600">
              Page {currentPage} of {Math.ceil(verses.length / versesPerPage)}
            </span>
            <button
              onClick={() => paginate(currentPage + 1)}
              disabled={currentPage === Math.ceil(verses.length / versesPerPage)}
              className={`p-2 rounded-full ${
                currentPage === Math.ceil(verses.length / versesPerPage) ? 'text-gray-400' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
            </button>
          </div>
          <button
            className="mt-2 text-xs text-blue-500 hover:underline focus:outline-none"
            onClick={() => setShowAll(true)}
          >
            See all verses without pagination
          </button>
        </div>
      )}
      {showAll && verses.length > versesPerPage && (
        <div className="mt-6 flex flex-col items-center">
          <button
            className="text-xs text-blue-500 hover:underline focus:outline-none"
            onClick={() => setShowAll(false)}
          >
            Show paginated view
          </button>
        </div>
      )}
    </div>
  );
};

export default SearchVerseSystem; 